import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress } from '@mui/material';
import { fetchWithAuth, API_ENDPOINTS } from '../config/api';

interface CurrentDocumentChatProps {
  fullText: string;
}

export default function CurrentDocumentChat({ fullText }: CurrentDocumentChatProps) {
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !currentUser || !fullText) {
      setResponse('Please upload a document and enter a message');
      return;
    }

    setIsLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetchWithAuth(API_ENDPOINTS.CHAT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message,
          context: fullText
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      setResponse(data.response);
      setMessage('');
    } catch (error) {
      console.error('Error:', error);
      setResponse('Error: Failed to get response from the server');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-8">
      <h2 className="text-2xl font-bold mb-4 text-dark-primary">Chat with Current Document</h2>
      <div className="space-y-6">
        {fullText ? (
          <div className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full p-2 rounded-md input-dark"
                rows={4}
                placeholder="Ask a question about your document..."
              />
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} className="text-white" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Send Message</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                    </svg>
                  </>
                )}
              </button>
            </form>
            {response && (
              <div className="p-4 card-dark rounded-md">
                <p className="whitespace-pre-wrap">{response}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-dark-secondary">
            Upload a document to start chatting
          </p>
        )}
      </div>
    </div>
  );
}
